import React from 'react';
import PropTypes from 'prop-types';
import './formattedText.scss';

/**
 * Parses simple markdown-like text and renders it with formatting
 * Supports: **bold**, *italic*, line breaks, bullet points, numbered lists
 */
const FormattedText = ({ text }) => {
    if (!text) return null;

    // Process inline formatting (bold and italic)
    const processInlineFormatting = (lineText) => {
        if (!lineText) return lineText;

        // Replace **bold** with styled spans
        // Replace *italic* with styled spans (but not **)
        const parts = [];
        let remaining = lineText;
        let keyIndex = 0;

        while (remaining.length > 0) {
            // Check for bold first (**)
            const boldIndex = remaining.indexOf('**');

            if (boldIndex !== -1) {
                const endBoldIndex = remaining.indexOf('**', boldIndex + 2);
                if (endBoldIndex !== -1) {
                    // Add text before bold
                    if (boldIndex > 0) {
                        parts.push(remaining.substring(0, boldIndex));
                    }
                    // Add bold text
                    const boldText = remaining.substring(boldIndex + 2, endBoldIndex);
                    parts.push(
                        <strong key={`b-${keyIndex}`} className="formatted-bold">
                            {boldText}
                        </strong>,
                    );
                    keyIndex += 1;
                    remaining = remaining.substring(endBoldIndex + 2);
                    // eslint-disable-next-line no-continue
                    continue;
                }
            }

            // Check for italic (single *)
            const italicIndex = remaining.indexOf('*');
            if (italicIndex !== -1 && remaining[italicIndex + 1] !== '*') {
                const endItalicIndex = remaining.indexOf('*', italicIndex + 1);
                if (endItalicIndex !== -1 && remaining[endItalicIndex - 1] !== '*') {
                    // Add text before italic
                    if (italicIndex > 0) {
                        parts.push(remaining.substring(0, italicIndex));
                    }
                    // Add italic text
                    const italicText = remaining.substring(italicIndex + 1, endItalicIndex);
                    parts.push(
                        <em key={`i-${keyIndex}`} className="formatted-italic">
                            {italicText}
                        </em>,
                    );
                    keyIndex += 1;
                    remaining = remaining.substring(endItalicIndex + 1);
                    // eslint-disable-next-line no-continue
                    continue;
                }
            }

            // No more formatting, add rest as plain text
            parts.push(remaining);
            break;
        }

        return parts.length > 0 ? parts : lineText;
    };

    // Process each line
    const processLine = (line, index) => {
        // Empty line = spacer
        if (line.trim() === '') {
            return <div key={index} className="formatted-spacer" />;
        }

        // Bullet points (- or •)
        const bulletMatch = line.match(/^\s*[-•]\s+(.*)$/);
        if (bulletMatch) {
            return (
                <div key={index} className="formatted-bullet">
                    <span className="bullet">•</span>
                    <span>{processInlineFormatting(bulletMatch[1])}</span>
                </div>
            );
        }

        // Numbered lists (1. or 1))
        const numberedMatch = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
        if (numberedMatch) {
            return (
                <div key={index} className="formatted-numbered">
                    <span className="number">{numberedMatch[1]}.</span>
                    <span>{processInlineFormatting(numberedMatch[2])}</span>
                </div>
            );
        }

        // Headers (# ## ###)
        const headerMatch = line.match(/^(#{1,3})\s+(.*)$/);
        if (headerMatch) {
            return (
                <div key={index} className="formatted-header">
                    {processInlineFormatting(headerMatch[2])}
                </div>
            );
        }

        // Regular paragraph
        return (
            <p key={index} className="formatted-paragraph">
                {processInlineFormatting(line)}
            </p>
        );
    };

    const lines = text.split('\n');

    return (
        <div className="formatted-text">
            {lines.map((line, index) => processLine(line, index))}
        </div>
    );
};

FormattedText.propTypes = {
    text: PropTypes.string,
};

FormattedText.defaultProps = {
    text: '',
};

export default FormattedText;
